import { serializer } from '@/api/shared/serializer';
import { FamilyHubAppStatus } from '@/enums/FamilyHubAppStatus';
import { FamilyResidentAccess } from '@/enums/FamilyResidentAccess';
import { FamilyResidentSettings } from '@/enums/FamilyResidentSettings';
import { batchUpdate } from '@/utils/batchUpdate';
import { familyResidentSettingsTypeMap, getDefaultFamilyResidentAccess, getDefaultFamilyResidentSettings } from '@/utils/familyResidentAccess';
import { cutString } from '@/utils/stringFormat';
import * as firebase from 'firebase/app';

export default {
  name: 'InviteUsersDialog',
  components: {},
  data() {
    return {
      dialog: false,
      loading: true,
      importing: false,
      uninvitedUsers: [],
      headers: [],
      headerCheckboxes: {
        invitedToFamilyApp: false,
        epoa: false,
        isCanInvite: false,
        residentConsent: false,
        care: false,
        dailyPlan: false,
        dailyCare: false,
        careSummary: false,
        carePlan: false,
        concierge: false,
        scheduledCalls: false,
        chat: false
      }
    };
  },
  computed: {
    anySelected() {
      return this.uninvitedUsers.some(x => x.invitedToFamilyApp === true);
    },
    facility(){
      return this.$store.state.facilityModule.selectedFacility;
    }
  },
  methods: {
    async open() {
      this.dialog = true;
      this.generateHeaders();

      this.uninvitedUsers = (
        await Promise.all(
          serializer
            .querySnapshotToDocuments<any>(
              await firebase
                .firestore()
                .collection('familyHubUsers')
                .where('facilityId', '==', this.facility.id)
                .where('appStatus', '==', FamilyHubAppStatus.NotAdded)
                .get()
            )
            .filter(family => !family.invitationSent && !family.invitedToFamilyApp)
            .map(async family => {
              const residents = Array.isArray(family.residents) ? family.residents : [];
      
              // Filter out residents without IDs
              const validResidents = residents.filter(resident => resident && resident.id);
      
              const residentItems = await Promise.all(
                validResidents.map(async resident => ({
                  ...family,
                  residentName: resident.name,
                  residentId: resident.id,
                  rowKey: `${family.id}_${resident.id}`,
                  familyResidentAccess: await this.buildFamilyResidentAccessMap(family.id, resident.id),
                  familyResidentSettings: await this.buildFamilyResidentSettingsMap(family.id, resident.id),
                  epoa: family?.enduringPOA?.find(x=>x.id === resident.id)?.status || false
                }))
              );
      
              return residentItems;
            })
        )
      ).flat().sort((a: any, b: any) => a?.email?.localeCompare(b?.email));
      this.loading = false;
    },
    async buildFamilyResidentAccessMap(memberId: string, residentId: string): Promise<Record<string, boolean>> {

      // Validate required parameters
      if (!this.facility?.id) {
        console.error('Facility ID is undefined');
        return getDefaultFamilyResidentAccess(this.facility, false);
      }
      
      if (!residentId) {
        console.error('Resident ID is undefined');
        return getDefaultFamilyResidentAccess(this.facility, false);
      }

      const snapshot = await firebase
        .firestore()
        .collection('familyHubUsers')
        .doc(memberId)
        .collection('familyResidentAccess')
        .where('facilityId', '==', this.facility.id)
        .where('residentId', '==', residentId)
        .get();

      const accessList = serializer.querySnapshotToDocuments<any>(snapshot);
    
      if (!accessList.length) {
        return getDefaultFamilyResidentAccess(this.facility, false);
      }

      const settings: Record<string, boolean> = {};
    
      accessList.forEach(data => {
        if (data?.name && typeof data.isEnabledByStaff === 'boolean') {
          settings[data.name] = data.isEnabledByStaff;
        }
      });
    
      return settings;
    },
    async buildFamilyResidentSettingsMap(memberId: string, residentId: string): Promise<Record<string, boolean>> {

      const snapshot = await firebase
        .firestore()
        .collection('familyHubUsers')
        .doc(memberId)
        .collection('familyResidentSettings')
        .where('facilityId', '==', this.facility.id)
        .where('residentId', '==', residentId)
        .get();
    
      const settingsList = serializer.querySnapshotToDocuments<any>(snapshot);
    
      if (!settingsList.length) {
        return getDefaultFamilyResidentSettings();
      }

      const settings: Record<string, boolean> = {};
    
      settingsList.forEach(data => {
          settings[data.name] = data.value;
      });
    
      return settings;
    },
    generateHeaders() {
      this.headers = [
        {
          text: '',
          align: '',
          sortable: false,
          value: 'invitedToFamilyApp',
        },
        {
          text: 'Name',
          align: '',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Resident',
          align: '',
          sortable: true,
          value: 'residentName',
        },
        {
          text: 'EPOA',
          align: '',
          sortable: false,
          value: 'epoa',
        },
      ];

      if(this.facility.canInviteFamilyMembers) {
        this.headers.push({
          text: 'Can invite',
          align: '',
          sortable: false,
          value: 'isCanInvite',
        })
      }

      if(this.facility.residentConsentCheckbox) {
        this.headers.push({
          text: 'Resident consent',
          align: '',
          sortable: false,
          value: FamilyResidentSettings.ResidentConsent,
        })
      }

      if(this.facility.isEnableCareTab) {
        this.headers.push({
          text: 'Care tab',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.Care,
        })
      }

      if(this.facility.isEnableCareTab && this.facility.isDailyPlan) {
        this.headers.push({
          text: 'Daily plan',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.DailyPlan,
        })
      }

      if(this.facility.isEnableCareTab && this.facility.isDailyCare) {
        this.headers.push({
          text: 'Daily care',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.DailyCare,
        })
      }

      if(this.facility.isEnableCareTab && this.facility.isCareSummary) {
        this.headers.push({
          text: 'Care summary',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.CareSummary,
        })
      }

      if(this.facility.isEnableCareTab || this.facility.isEnabledCarePlan) {
        this.headers.push({
          text: 'Care plan',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.CarePlan,
        })
      }

      if(this.facility.additionalServicesEnabled) {
        this.headers.push({
          text: 'Concierge',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.Concierge,
        })
      }

      if(this.facility.scheduledCallsEnabled) {
        this.headers.push({
          text: 'Scheduled calls',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.ScheduledCalls,
        })
      }

      if(this.facility.familyChatEnabled) {
        this.headers.push({
          text: 'Chat',
          align: '',
          sortable: false,
          value: FamilyResidentAccess.Chat,
        })
      }
    },
    close() {
      this.loading = false;
      this.importing = false;
      this.dialog = false;
      this.headers = [];
      this.headerCheckboxes = {
        invitedToFamilyApp: false,
        epoa: false,
        isCanInvite: false,
        residentConsent: false,
        care: false,
        dailyPlan: false,
        dailyCare: false,
        careSummary: false,
        carePlan: false,
        concierge: false,
        scheduledCalls: false,
        chat: false
      };
    },
    async importUsers() {
      this.importing = true;

      const selectedItems = this.uninvitedUsers.filter(x => x.invitedToFamilyApp === true)
      const itemsToUpdate = []

      selectedItems.forEach(item => {
      
        if(!itemsToUpdate.some(x=>x.docId === item.id)){

          const enduringPOA = selectedItems.filter(x => x.id === item.id && x.epoa === true).map(x => ({ id: x.residentId, status: true }));

          itemsToUpdate.push({
            docId: item.id,
            updates: { 
              appStatus: "Pending",
              invitedToFamilyApp: true, 
              invitationSent: true,
              isCanInvite: item.isCanInvite,
              enduringPOA: enduringPOA
            }
          })
          console.log(`member: ${item.name}  resident: ${item.residentName}` )
        }
      });

      console.log('itemsToUpdate: ', itemsToUpdate);

      await batchUpdate('familyHubUsers', itemsToUpdate).then(() => {
          this.$toast.open({
            message: `Invitation has been sent to ${itemsToUpdate.length} family members${
              itemsToUpdate.length > 1 ? 's' : ''
            }`,
            type: 'success'
          });
          this.close();
        })
        .catch(error => {
          this.close();
          this.$toast.open({
            message: error,
            type: 'Unhandled error',
          });
        });

        try {
          for (const item of selectedItems) {
            const userId = item.id;
        
            const residents = this.uninvitedUsers.filter(x => x.id === userId);
        
            for (const residentItem of residents) {
              const residentId = residentItem.residentId;
              const accessMap = residentItem.familyResidentAccess || {};
              console.log(`familyHubUsers: ${item.id} residentId: ${residentId}`);
        
              for (const [name, isEnabledByStaff] of Object.entries(accessMap)) {
                const accessDoc = {
                  name,
                  isEnabledByStaff,
                  isEnabled: isEnabledByStaff,
                  facilityId: this.facility.id,
                  residentId,
                };
        
                const docId = `${residentId}_${name}`;
        
                await firebase.firestore()
                  .collection('familyHubUsers')
                  .doc(userId)
                  .collection('familyResidentAccess')
                  .doc(docId)
                  .set(accessDoc, { merge: true });
              }
            }
          }
        } catch (error) {
          console.error(error);
          this.$toast.open({
            message: 'Some resident access was not set or updated',
            type: 'error',
          });
        }

        try {
          for (const item of selectedItems) {
            const userId = item.id;
        
            const residents = this.uninvitedUsers.filter(x => x.id === userId);
        
            for (const residentItem of residents) {
              const residentId = residentItem.residentId;
              const accessMap = residentItem.familyResidentSettings || {};
        
              for (const [name, value] of Object.entries(accessMap)) {
                const accessDoc = {
                  name,
                  value,
                  type: familyResidentSettingsTypeMap[name],
                  facilityId: this.facility.id,
                  residentId,
                };
        
                const docId = `${residentId}_${name}`;

                await firebase.firestore()
                  .collection('familyHubUsers')
                  .doc(userId)
                  .collection('familyResidentSettings')
                  .doc(docId)
                  .set(accessDoc, { merge: true });
              }
            }
          }
        } catch (error) {
          console.error(error);
          this.$toast.open({
            message: 'Some resident settings was not set or updated',
            type: 'error',
          });
        }
    },
    updateIsCanInviteForAll(userId, value) {
      this.uninvitedUsers.forEach(user => {
        if (user.id === userId) {
          this.$set(user, 'isCanInvite', value);
        }
      });
    },
    onEpoaChanged(item){
      if(!item.epoa){
        this.setAccessEnabled(item,'carePlan', false)
      }
    },
    getAccessEnabled(item, name) {
      return item.familyResidentAccess?.[name] ?? false;
    },
    setAccessEnabled(item, name, value) {
      if (!item.familyResidentAccess) {
        item.familyResidentAccess = {};
      }
      if(name === FamilyResidentAccess.Care && value === false){
        this.toggleCareTabOptions(false, item);
      }

      this.$set(item.familyResidentAccess, name, value);

      if(item.familyResidentAccess?.dailyPlan === true || item.familyResidentAccess?.dailyCare === true || item.familyResidentAccess?.careSummary === true || item.familyResidentAccess?.carePlan === true){
        this.$set(item.familyResidentAccess, FamilyResidentAccess.Care, true);
      }
      else if(!item.familyResidentAccess?.dailyPlan && !item.familyResidentAccess?.dailyCare && !item.familyResidentAccess?.careSummary && !item.familyResidentAccess?.carePlan){
        this.$set(item.familyResidentAccess, FamilyResidentAccess.Care, false);
      }
    },
    getSettingEnabled(item, name) {
      return item.familyResidentSettings?.[name] ?? false;
    },
    setSettingEnabled(item, name, value) {
      if (!item.familyResidentSettings) {
        item.familyResidentSettings = {};
      }
      this.$set(item.familyResidentSettings, name, value);
    },
    selectAll(value){
        this.uninvitedUsers = this.uninvitedUsers.map(user => ({
            ...user,
            import: value
        }));
    },
    toggleColumnCheckbox(column, source, value) {
      this.uninvitedUsers.forEach(user => {
        if (source === 'root') {
          user[column] = value;
        } else if (source === 'familyResidentAccess') {
          if(value === false) this.toggleCareTabOptions(false, user);
          
          this.$set(user[source], column, value);
        } else {
          if (!user[source]) {
            this.$set(user, source, {});
          }
          this.$set(user[source], column, value);
        }
      });
    },
    toggleCareTabOptions(value: boolean,user: any){
        this.$set(user.familyResidentAccess, FamilyResidentAccess.DailyPlan, value);
        this.$set(user.familyResidentAccess, FamilyResidentAccess.DailyCare, value);
        this.$set(user.familyResidentAccess, FamilyResidentAccess.CareSummary, value);
        this.$set(user.familyResidentAccess, FamilyResidentAccess.CarePlan, value);
    },
    cutText(str) {
      return cutString(str, 25)
    }
  }
};
